import React, { useEffect, useState } from 'react';
import Button from '../../components/common/Button';
import FormField from '../../components/common/FormField';
import Form from '../../components/common/Form';
import FormSection from '../../components/common/FormSection';
import { GradingCategoryDTO, GradingTypeCategoryDTO, GradingTypeDTO } from '../../services/apiClient';
import handleChange from '../../utils/formUtils/handleChange';
import { ValidationRules, useFormValidation } from '../../hooks/useFormValidation';
import { useGradingCategoryApi } from '../../hooks/useGradingCategoryApi';
import { Pencil, Trash2 } from 'lucide-react';
import Badge from '../common/Badge';
import Drawer from '../common/Drawer';
import GradingTypeCategoryForm from './GradingTypeCategoryForm';
import AddButton from '../common/AddButton';

interface GradingTypeFormProps {
  initialValues: GradingTypeDTO;
  onSubmit: (data: GradingTypeDTO) => void;
  onCancel: () => void;
}

const FORM_SECTIONS = {
  basicInfo: {
    id: 'basic-info',
    title: 'Basic Information',
  },
  settings: {
    id: 'settings',
    title: 'Settings',
  },
  categories: {
    id: 'categories',
    title: 'Categories',
  },
} as const;

const GradingTypeForm: React.FC<GradingTypeFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
}) => {
  const [formData, setFormData] = useState<GradingTypeDTO>(initialValues);
  const [gradingCategories, setGradingCategories] = useState<GradingCategoryDTO[]>([]);
  const [isExerciseDrawerOpen, setIsExerciseDrawerOpen] = useState(false);
  const [selectedExercise, setSelectedExercise] = useState<{
    exerciseIndex: number;
    specific: GradingTypeCategoryDTO;
  } | null>(null);

const gradingCategoryApi = useGradingCategoryApi();

  const validationRules: ValidationRules = {
    title: [
      { required: true, message: 'Title is required' },
    ],
    displayPosition: [
      { required: true, message: 'Position is required' },
      { 
        custom: (value) => Number(value) >= 0, 
        message: 'Position must be a positive number' 
      }
    ]
  };

  const { errors, validate } = useFormValidation<GradingTypeDTO>(validationRules);

  const handleAddExercise = (exercise: GradingTypeCategoryDTO) => {
    setFormData((prev) => ({
      ...prev,
      gradingCategories: [...(prev.gradingCategories || []), exercise],
    }));
    setIsExerciseDrawerOpen(false);
  };

  const handleExerciseSubmit = (exercise: GradingTypeCategoryDTO) => {
    if (selectedExercise) {
      // Update existing exercise
      setFormData((prev) => ({
        ...prev,
        gradingCategories: prev.gradingCategories?.map((e, i) =>
          i === selectedExercise.exerciseIndex ? exercise : e,
        ),
      }));
    } else {
      // Add new exercise
      setFormData((prev) => ({
        ...prev,
        gradingCategories: [...(prev.gradingCategories || []), exercise],
      }));
    }
    setIsExerciseDrawerOpen(false);
    setSelectedExercise(null);
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        const categoriesResponse = await gradingCategoryApi.search({
          page: 1,
          pageSize: 9999,
          sortBy: 'DisplayPosition',
          sortDescending: false,
        });
        setGradingCategories(categoriesResponse?.data?.results || []);
      } catch (error) {
        console.error('Error loading reference data:', error);
      }
    };

    loadData();
  }, []);

  const getUsedGradingcategoryIds = () => {
    return new Set(formData.gradingCategories?.map(e => e.gradingCategoryId) || []);
  };

  const handleSave = () => {
    if (validate(formData)) {
      onSubmit(formData);
    }
  };

  const sections = Object.values(FORM_SECTIONS);

  return (
    <Form sections={sections} onSave={handleSave} onCancel={onCancel}>
      <FormSection
        id={FORM_SECTIONS.basicInfo.id}
        title={FORM_SECTIONS.basicInfo.title}
      >
        <FormField
          id="title"
          label="Title"
          type="text"
          value={formData.title || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Title"
          required
          error={errors.title}
        />
      </FormSection>

      <FormSection
        id={FORM_SECTIONS.settings.id}
        title={FORM_SECTIONS.settings.title}
      >
        <FormField
          id="displayPosition"
          label="Position"
          type="number"
          value={formData.displayPosition ?? ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Display Position"
          helper="Lower numbers will be displayed first."
          required
          error={errors.displayPosition}
        />
        <FormField
          id="isActive"
          label="Active?"
          type="checkbox"
          value={formData.isActive || ''}
          onChange={(e) => handleChange(e, setFormData)}
          helper="Check to make record active."
        />
      </FormSection>

      <FormSection
        id={FORM_SECTIONS.categories.id}
        title={FORM_SECTIONS.categories.title}
        headerActions={
          gradingCategories.length > (formData.gradingCategories?.length || 0) ? (
            <AddButton
              onClick={() => setIsExerciseDrawerOpen(true)}
              variant="secondary"
              label="Add category"
            />
          ) : (
            <AddButton
              onClick={() => {}}
              variant="secondary"
              label="All categories have been added"
              disabled
            />
          )
        }
      >
        {formData.gradingCategories && formData.gradingCategories.length > 0 ? (
          <div className="space-y-4">
            {formData.gradingCategories.map((gradingCategory, index) => (
              <div key={index} className="p-4 bg-gray-50 rounded">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-lg">
                    {
                      gradingCategories.find(
                        (t) => t.id === gradingCategory.gradingCategoryId,
                      )?.title
                    }
                  </span>
                  <div className="flex items-center space-x-2">
                    <button
                      type="button"
                      className="p-2 text-gray-600 hover:text-blue-600 transition-colors"
                      onClick={() => {
                        setIsExerciseDrawerOpen(true);
                        setSelectedExercise({
                          exerciseIndex: index,
                          specific: gradingCategory,
                        });
                      }}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      className="p-2 text-gray-600 hover:text-red-600 transition-colors"
                      onClick={() => {
                        setFormData((prev) => ({
                          ...prev,
                          gradingCategories: prev.gradingCategories?.filter(
                            (_, i) => i !== index,
                          ),
                        }));
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {gradingCategory.gradingExerciseSpecifics &&
                gradingCategory.gradingExerciseSpecifics.length > 0 ? (
                  <div className="ml-4 space-y-2">
                    {gradingCategory.gradingExerciseSpecifics.map((specific) => (
                      <div
                        key={specific.id}
                        className="flex items-center justify-between p-2 bg-white rounded border border-gray-200"
                      >
                        <div className="flex flex-col space-y-2">
                          <span className="font-medium">{specific.title}</span>
                          <div className="flex flex-wrap gap-2">
                            {specific.isComplianceHunt && (
                              <Badge
                                label="Hunt Compliance"
                                variant="success"
                              />
                            )}
                            {specific.isComplianceSport && (
                              <Badge
                                label="Sport Compliance"
                                variant="success"
                              />
                            )}
                            {specific.isEvaluationHunt && (
                              <Badge
                                label="Hunt Evaluation"
                                variant="success"
                              />
                            )}
                            {specific.isEvaluationSport && (
                              <Badge
                                label="Sport Evaluation"
                                variant="success"
                              />
                            )}
                            {specific.isCompetition && (
                              <Badge label="Competition" variant="success" />
                            )}
                            {specific.isHuntActivity && (
                              <Badge label="Hunt Activity" variant="success" />
                            )}
                            {specific.isSportShootingActivity && (
                              <Badge label="Sport Shooting" variant="success" />
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm ml-4">
                    No grading categories linked.
                  </p>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-sm">No grading categories linked yet.</p>
        )}
      </FormSection>

      <Drawer
        isOpen={isExerciseDrawerOpen}
        onClose={() => {
          setIsExerciseDrawerOpen(false);
          setSelectedExercise(null);
        }}
        title={selectedExercise ? 'Edit Category' : 'Add Category'}
      >
        <GradingTypeCategoryForm
          onSubmit={handleExerciseSubmit}
          onCancel={() => {
            setIsExerciseDrawerOpen(false);
            setSelectedExercise(null);
          }}
          initialValues={selectedExercise?.specific}
          availableGradingCategories={
            selectedExercise 
              ? gradingCategories 
              : gradingCategories.filter(t => !getUsedGradingcategoryIds().has(t.id))
          }
        />
      </Drawer>
    </Form>
  );
};

export default GradingTypeForm;
